import React from "react";
import styles from "./styles.module.scss";

type Props = {
  currentPage: number;
  pageCount: number;
  // Absolute href for a given 1-based page.
  buildHref: (page: number) => string;
};

const DOTS = "...";

// Windowed page list: always first + last, current +/- 1, widened to 3 at the
// edges, with ellipses for gaps. e.g. page 1 of 34 -> [1, 2, 3, "...", 34].
export const getPaginationItems = (current: number, total: number): (number | string)[] => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set<number>([1, total, current, current - 1, current + 1]);
  if (current <= 3) {
    pages.add(2);
    pages.add(3);
  }
  if (current >= total - 2) {
    pages.add(total - 1);
    pages.add(total - 2);
  }
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const items: (number | string)[] = [];
  let prev = 0;
  for (const page of sorted) {
    if (prev && page - prev > 1) {
      items.push(DOTS);
    }
    items.push(page);
    prev = page;
  }
  return items;
};

export const Pagination = ({ currentPage, pageCount, buildHref }: Props) => {
  if (!pageCount || pageCount <= 1) {
    return null;
  }
  const items = getPaginationItems(currentPage, pageCount);

  return (
    <nav className={styles.pagination} aria-label="Related news pagination">
      <div className={styles.pages}>
        {items.map((item, index) =>
          typeof item === "string" ? (
            <span key={`dots-${index}`} className={`${styles.item} ${styles.dots}`} aria-hidden="true">
              {DOTS}
            </span>
          ) : item === currentPage ? (
            <span key={item} className={`${styles.item} ${styles.active}`} aria-current="page">
              {item}
            </span>
          ) : (
            <a key={item} className={styles.item} href={buildHref(item)} aria-label={`Page ${item}`}>
              {item}
            </a>
          )
        )}
        {currentPage < pageCount && (
          // Chevron-only per the design; the label carries the accessible name.
          <a
            className={`${styles.item} ${styles.next}`}
            href={buildHref(currentPage + 1)}
            rel="next"
            aria-label="Next page"
          >
            &rsaquo;
          </a>
        )}
      </div>
      <p className={styles.count}>
        Page {currentPage} of {pageCount}
      </p>
    </nav>
  );
};
